import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, CustomExtendedDataField } from '../../../components';
import { ReactComponent as AppleIcon } from '../../../assets/icons/apple.svg';

import css from './ConfirmSignupForm.module.css';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';
import { useConfiguration } from '../../../context/configurationContext';
import MarketingConsent from '../MarketingConsent/MarketingConsent';
import TermsAndConditions from '../TermsAndConditions/TermsAndConditions';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';
import arrayMutators from 'final-form-arrays';

const KEY_CODE_ENTER = 13;

const ConfirmAppleFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        confirmAppleInProgress,
        isEmail,
        idp,
      } = formRenderProps;
      // email
      const emailLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.emailLabel',
      });
      const emailPlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      // firstName
      const firstNameLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.firstNameLabel',
      });
      const firstNamePlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.firstNamePlaceholder',
      });
      const firstNameRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.firstNameRequired',
      });
      const firstNameRequired = validators.required(firstNameRequiredMessage);

      // lastName
      const lastNameLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.lastNameLabel',
      });
      const lastNamePlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.lastNamePlaceholder',
      });
      const lastNameRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.lastNameRequired',
      });
      const lastNameRequired = validators.required(lastNameRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;
      const config = useConfiguration();
      const { userFields, userTypes } = config.user;
      const userType = "non-business-user"
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);
      const usernameField = userFieldProps.find(item => item.fieldConfig.key === 'username');
      const userTypeConfig = userTypes.find(config => config.userType === userType);


      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.defaultUserFields}>
            <div className={css.emailAndNameRow}>
              <FieldTextInput
                className={css.emailField}
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={`${emailLabel} *`}
                placeholder={emailPlaceholder}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />

              <FieldTextInput
                className={css.fullNameField}
                type="text"
                id={formId ? `${formId}.fullName` : 'fullName'}
                name="fullName"
                autoComplete="name"
                label="Vollständiger Name *"
                placeholder={intl.formatMessage({
                  id: 'ConfirmSignupForm.fullNamePlaceholder',
                  defaultMessage: 'z.B. Max Mustermann',
                })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'ConfirmSignupForm.fullNameRequired',
                    defaultMessage: 'Name ist erforderlich',
                  })
                )}
              />
            </div>

            {usernameField && (
              <CustomExtendedDataField
                key={usernameField.key}
                fieldConfig={usernameField.fieldConfig}
                name={usernameField.name}
                formId={formId}
                defaultRequiredMessage={usernameField.defaultRequiredMessage}
                className={css.row}
              />
            )}

            <UserFieldDisplayName
              formName="ConfirmSignupForm"
              className={css.row}
              userTypeConfig={userTypeConfig}
              intl={intl}
            />
            <UserFieldPhoneNumber
              formName="ConfirmSignupForm"
              className={css.row}
              userTypeConfig={userTypeConfig}
              intl={intl}
            />
          </div>

          <div className={css.bottomWrapper}>
            <TermsAndConditions intl={intl} />
            <MarketingConsent />
            <PrimaryButton
              type="submit"
              inProgress={confirmAppleInProgress}
              disabled={submitDisabled}
              className={css.submitButton}
            >
              <AppleIcon className={css.appleIcon} />
              <span className={css.buttonText}>
                <FormattedMessage id="ConfirmSignupForm.signUp" values={{ idp: idp }} />
              </span>
            </PrimaryButton>
          </div>
        </Form >
      );
    }}
  />
);

ConfirmAppleFormComponent.defaultProps = { inProgress: false };

const { bool, func } = PropTypes;

ConfirmAppleFormComponent.propTypes = {
  inProgress: bool,
  // from injectIntl
  intl: intlShape.isRequired,
};

const ConfirmAppleForm = compose(injectIntl)(ConfirmAppleFormComponent);
ConfirmAppleForm.displayName = 'ConfirmAppleForm';

export default ConfirmAppleForm;
